/**
 * TipoObraField · Wave 14 (Onda C) · 2026-05-19 · B10 end-to-end
 *
 * Select reusável pra `projects.tipo_obra` no form de criar/editar obra.
 *
 * Por quê opcional? O PM novo abre o app pra orçar uma obra; o tipo dela
 * é um sinal a mais — útil mas não bloqueante. Forçar a escolha logo de
 * cara quebraria a "fluidez e facilidade" do `OPERATING-MANUAL.md`.
 *
 * Quando preenchido, destrava B10 end-to-end: o `TrenaWizardPage` lê
 * `project.tipoObra` e passa pro `TrenaTemplatePickerDrawer`, que então
 * agrupa templates específicos no topo via `?obra_type=`.
 *
 * Aceita `null` no `value` (estado "não informado"). `onChange` recebe
 * `TipoObra | null` — passar null no submit final pra API equivale a
 * "limpar" no PATCH, ou "não informado" no POST.
 *
 * Toque mínimo 44pt em mobile (h-11 = 44px) por exigência do OM.
 */

import type { TipoObra } from '@builder/shared';
import { tipoObraSchema } from '@builder/shared';
import { cn } from '@/lib/cn';
import { useT } from '@/hooks/useT';

interface TipoObraFieldProps {
  value: TipoObra | null;
  onChange: (next: TipoObra | null) => void;
  /**
   * Quando true (default), label fica "Tipo de obra (opcional)" — usado
   * no fluxo de criação onde fica claro que o campo não é obrigatório.
   * No PATCH é redundante; passar false ali pra usar só "Tipo de obra".
   */
  showOptionalHint?: boolean;
  /** Erro de validação inline (ex.: "Tipo inválido"). */
  error?: string;
  /** Disabled durante submit pra evitar double-write. */
  disabled?: boolean;
  /** ID HTML — defaults to 'project-tipo-obra'. */
  id?: string;
  /** data-testid hook pra tests. */
  testId?: string;
}

/**
 * Ordem fixa segue a granularidade da obra (popular → médio → comercial
 * → industrial). Tirando do Zod schema garante drift zero com o backend
 * — qualquer entry nova no enum aparece aqui sem edição manual.
 */
const TIPO_OBRA_VALUES = tipoObraSchema.options;

export function TipoObraField({
  value,
  onChange,
  showOptionalHint = true,
  error,
  disabled,
  id = 'project-tipo-obra',
  testId = 'tipo-obra-field',
}: TipoObraFieldProps): JSX.Element {
  const { t } = useT();
  const labelText = showOptionalHint ? t.obraType.optional : t.obraType.label;

  return (
    <label className="block space-y-1.5" htmlFor={id}>
      <span className="text-sm font-medium text-ink">{labelText}</span>
      <select
        id={id}
        data-testid={testId}
        value={value ?? ''}
        disabled={disabled}
        onChange={(e) => {
          const raw = e.currentTarget.value;
          if (!raw) {
            onChange(null);
            return;
          }
          // tipoObraSchema valida → garante que valor inválido nunca sai
          // do select pro form. Defensive parse (não deve falhar nunca
          // porque as <option> só listam o enum, mas mantém boundary
          // explícito caso alguém estenda via DevTools).
          const parsed = tipoObraSchema.safeParse(raw);
          onChange(parsed.success ? parsed.data : null);
        }}
        className={cn(
          'w-full rounded-xl border bg-surface-2 text-ink',
          'border-line transition-colors duration-160 ease-out-soft',
          'focus:border-brand-400 focus:ring-2 focus:ring-brand-400/25 focus:outline-none',
          'h-11 px-3.5 text-[15px]',
          'disabled:opacity-55 disabled:cursor-not-allowed',
          error && 'border-err focus:border-err focus:ring-err/25',
        )}
        aria-invalid={error ? true : undefined}
      >
        <option value="">{t.obraType.placeholder}</option>
        {TIPO_OBRA_VALUES.map((v) => (
          <option key={v} value={v}>
            {t.obraType[v]}
          </option>
        ))}
      </select>
      {error ? (
        <span className="text-xs text-err">{error}</span>
      ) : (
        <span className="text-xs text-ink-subtle">{t.obraType.helperText}</span>
      )}
    </label>
  );
}
